import { useTranslation } from 'react-i18next';
import { formatIntNumber } from '@anytag/shared-utils';
import { InfluencerAvatar } from '@anytag/shared-molecules';
import { useGenerateInfluencerProfileLink, usePostDetailsParam } from '@anytag/shared-hooks';
import { Anchor, MediaPreview, TableCell } from '@anytag/shared-atoms';
import { InstagramPostSortOrder, InstagramHashtagFeedPostV2, SocialAccountType } from '@anytag/graphql';
import { ColumnDef } from '@tanstack/react-table';

export const useAnalyticsInstagramHashtagPostsListTable = () => {
  const { t } = useTranslation();
  const { setPostDetailsParam } = usePostDetailsParam();
  const { generateProfileLink } = useGenerateInfluencerProfileLink();

  const columns: ColumnDef<InstagramHashtagFeedPostV2>[] = [
    {
      size: 68,
      accessorKey: 'rank',
      header: t<string>('HeaderColumn.Rank'),
      meta: { align: 'center', sortField: InstagramPostSortOrder.RANK },
      cell: ({ row }) => <div className="pr-2">{formatIntNumber(row.original.rank + 1)}</div>
    },
    {
      accessorKey: 'influencer',
      header: t<string>('HeaderColumn.Influencer'),
      cell: ({ row }) => {
        const { influencerId, accountId, avatar, name, followersCount } = row.original;
        const profileLink = generateProfileLink(influencerId, {
          filter: {
            id: String(accountId),
            sm: SocialAccountType.INSTAGRAM
          }
        });

        return (
          <div className="flex w-50 items-center gap-2">
            <InfluencerAvatar {...profileLink} size="40px" src={avatar} className="rounded-none" />
            <div>
              <Anchor {...profileLink} label={name || '-'} className="text-base" />
              <div className="mt-1 text-xxs">
                {t('Follower')}:&nbsp;{formatIntNumber(followersCount)}
              </div>
            </div>
          </div>
        );
      }
    },
    {
      accessorKey: 'post',
      header: t<string>('HeaderColumn.Post'),
      cell: ({ row }) => {
        const { id, images, content } = row.original;

        return (
          <div onClick={() => setPostDetailsParam(id)} className="flex min-w-100 cursor-pointer items-center gap-2">
            <MediaPreview
              src={images.at(0)}
              objectFit="contain"
              className="size-14 rounded-[5px] bg-light-gray"
              counter={{ index: 1, total: images.length }}
            />
            <Anchor lines={3} label={content || '-'} />
          </div>
        );
      }
    },
    {
      accessorKey: 'engagement',
      header: t<string>('HeaderColumn.Engagement'),
      meta: { align: 'right', sortField: InstagramPostSortOrder.ENGAGEMENT },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.engagement)} />
    },
    {
      accessorKey: 'likes',
      header: t<string>('HeaderColumn.Like'),
      meta: { align: 'right', sortField: InstagramPostSortOrder.LIKES },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.likes)} />
    },
    {
      accessorKey: 'comments',
      header: t<string>('HeaderColumn.Comment'),
      meta: { align: 'right', sortField: InstagramPostSortOrder.COMMENTS },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.comments)} />
    },
    {
      accessorKey: 'postDate',
      header: t<string>('HeaderColumn.Post date'),
      meta: { sortField: InstagramPostSortOrder.POST_DATE },
      cell: ({ row }) => <TableCell.Date value={row.original.postDate} />
    }
  ];

  return { columns };
};
